import React, { useState } from "react";
import { addSandboxFiles, openFile, readmePath, sandboxFilesSelector, sandboxReadmeSelector, sandboxWalkthroughSelector, selectCurrentUser } from "store/features";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { SvgChevronDown } from "@itwin/itwinui-icons-react";
import { IconButton, Tab, Tabs, Tooltip } from "@itwin/itwinui-react";
import { CodeEditorInstance, ErrorCountBadge, ErrorList, FilePath, PackageVersions, ScrollbarVertical, TestReportViewer } from "@sandbox/code-editor";
import { createReadmeStarter, Readme } from "@sandbox/markdown";
import styles from "./CodePanels.module.css";
import { Walkthrough } from "./walkthrough/Walkthrough";

interface CodePanelsProps {
  editor: CodeEditorInstance;
  onCollapsed?: () => void;
}

type TabItemType = "problems" | "readme" | "walk" | "versions" | "tests";

interface TabLabel {
  element: JSX.Element;
  type: TabItemType;
};

export const CodePanels = ({ editor, onCollapsed }: CodePanelsProps) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const sandbox = useAppSelector((state) => state.activeSandbox.metadata);
  const packages = useAppSelector((state) => state.activeSandbox.resolvedPackages);
  const sourceFiles = useAppSelector(sandboxFilesSelector);
  const readmeFile = useAppSelector(sandboxReadmeSelector);
  const walkthroughFile = useAppSelector(sandboxWalkthroughSelector);
  const layout = useAppSelector((state) => state.settings.layout);
  const testReport = useAppSelector((state) => state.test.report);
  const [errorCount, setErrorCount] = useState(0);
  const [index, setIndex] = useState<TabItemType | undefined>(layout.overviewPanel !== "hidden" && readmeFile ? "readme" : "problems");

  const onErrorClicked = (filePath: FilePath, startLineNumber: number, endLineNumber: number, startColumn: number, endColumn: number) => {
    dispatch(openFile({ filePath, selection: { startLineNumber, endLineNumber, startColumn, endColumn } }));
  };

  const onReadmeCreate = () => {
    dispatch(addSandboxFiles({ filePath: readmePath, type: "file", content: createReadmeStarter(sourceFiles.length ? sourceFiles[0].filePath : "/test.ts") }));
    dispatch(openFile({ filePath: readmePath }));
  }

  const tabs = () => {

    const tabLabels: TabLabel[] = [];

    if (layout.problemsPanel !== "hidden")
      tabLabels.push({
        element: <Tab key={0} label={<ProblemsLabel errorCount={errorCount} />} className={styles.tabItem} />,
        type: "problems"
      })

    if (layout.overviewPanel !== "hidden" && (user?.id === sandbox?.userId || readmeFile))
      tabLabels.push({
        element: <Tab key={1} label="Overview" className={styles.tabItem} />,
        type: "readme"
      });

    if (layout.walkthroughPanel !== "hidden" && (user?.id === sandbox?.userId || walkthroughFile))
      tabLabels.push({
        element: <Tab key={2} label="Walkthrough" className={styles.tabItem} />,
        type: "walk"
      });

    if (layout.packagesPanel !== "hidden")
      tabLabels.push({
        element: <Tab key={3} label="Packages" className={styles.tabItem} />,
        type: "versions"
      });

    if (sourceFiles.some((item) => item.filePath.match(/\.spec\.(ts|tsx)$/))) {
      tabLabels.push({
        element: <Tab key={4} label="Tests" className={styles.tabItem} />,
        type: "tests"
      });
    }

    return tabLabels;
  };

  if (!tabs().length)
    return null;

  return (
    <div className={styles.container}>
      <Tabs
        labels={tabs().map((item) => item.element)}
        type="borderless"
        activeIndex={tabs().findIndex((item) => item.type === index)}
        onTabSelected={(index) => setIndex(tabs()[index].type)}
        wrapperClassName={styles.tabsContainer}
        tabsClassName={styles.tabItems}
        contentClassName={styles.tabsContent}
        actions={[
          <Tooltip key="collapse-btn" content={"Show file explorer"} placement="bottom" delay={500}>
            <IconButton styleType="borderless" onClick={onCollapsed}><SvgChevronDown /></IconButton>
          </Tooltip>
        ]}
      >
        <div>
          {/* need to put contents outside for scrollbar to work properly. Empty div is need for styling */}
        </div>
      </Tabs >
      <ScrollbarVertical className={index === "problems" ? styles.visible : styles.hidden}>
        <ErrorList
          sourceFiles={sourceFiles}
          onErrorClicked={onErrorClicked}
          onErrors={(errorCount) => setErrorCount(errorCount)}
        />
      </ScrollbarVertical>
      <ScrollbarVertical className={index === "readme" ? styles.visible : styles.hidden}>
        <Readme
          content={readmeFile?.content}
          readOnly={user?.id !== sandbox?.userId}
          onCreateReadme={onReadmeCreate}
        />
      </ScrollbarVertical>
      <Walkthrough
        className={index === "walk" ? styles.visible : styles.hidden}
        sandboxName={sandbox?.name}
        files={sourceFiles}
        editor={editor}
        readOnly={user?.id !== sandbox?.userId}
      />
      <PackageVersions
        className={index === "versions" ? styles.visible : styles.hidden}
        packages={packages as any}
      />
      <TestReportViewer
        report={testReport}
        className={index === "tests" ? styles.visible : styles.hidden}
      />
    </div >
  );
}

const ProblemsLabel = ({ errorCount }: { errorCount: number }) => {
  return <span className={styles.problemsTabItem}>
    <span>Problems</span>
    <ErrorCountBadge errorCount={errorCount} />
  </span>
}

export const CodePanelsMemo = React.memo(CodePanels);